import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { router } from '@inertiajs/react'
import fr from '../i18n/fr.json'
import en from '../i18n/en.json'

export type Locale = 'fr' | 'en'

const messages: Record<Locale, typeof fr> = { fr, en }

function resolve(obj: unknown, path: string): string {
  const parts = path.split('.')
  let cur: unknown = obj
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return path
    cur = (cur as Record<string, unknown>)[p]
  }
  return typeof cur === 'string' ? cur : path
}

function resolveArray(obj: unknown, path: string): string[] {
  const parts = path.split('.')
  let cur: unknown = obj
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return []
    cur = (cur as Record<string, unknown>)[p]
  }
  return Array.isArray(cur) ? cur : []
}

interface LocaleContextValue {
  locale: Locale
  toggle: () => void
  t: (key: string, vars?: Record<string, string | number>) => string
  ta: (key: string) => string[]
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

export function useLocaleProvider() {
  const [locale, setLocale] = useState<Locale>(() => {
    if (typeof window === 'undefined') return 'fr'
    return (localStorage.getItem('aegyl-locale') as Locale) || 'fr'
  })

  const isFirstRender = useRef(true)

  useEffect(() => {
    document.documentElement.setAttribute('lang', locale)
    localStorage.setItem('aegyl-locale', locale)
    document.cookie = `locale=${locale};path=/;max-age=31536000;SameSite=Lax`

    // Reload page data from server when locale changes (not on initial mount)
    if (isFirstRender.current) {
      isFirstRender.current = false
    } else {
      router.reload()
    }
  }, [locale])

  const toggle = useCallback(() => {
    setLocale((l) => (l === 'fr' ? 'en' : 'fr'))
  }, [])

  const t = useMemo(() => {
    const dict = messages[locale]
    return (key: string, vars?: Record<string, string | number>) => {
      let val = resolve(dict, key)
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          val = val.replace(`{${k}}`, String(v))
        }
      }
      return val
    }
  }, [locale])

  const ta = useMemo(() => {
    const dict = messages[locale]
    return (key: string): string[] => resolveArray(dict, key)
  }, [locale])

  return { locale, toggle, t, ta }
}

export { LocaleContext }

export function useLocale() {
  const ctx = useContext(LocaleContext)
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider')
  return ctx
}
